import { format, parseISO } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  CircleCheck,
  FileSpreadsheet,
  History,
  Info,
  TriangleAlert,
  Upload as UploadIcoon,
} from 'lucide-react'
import { useId, useRef, useState } from 'react'
import { Checkbox } from '@/components/ui/Checkbox'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { PageHeader } from '@/components/ui/PageHeader'
import { Skeleton } from '@/components/ui/Skeleton'
import { Uitklapper } from '@/components/ui/Uitklapper'
import { isOpvallend, verdeelMeldingen } from '@/lib/meldingen'
import {
  useBevestigVoorbeeld,
  useMaakVoorbeeld,
  useUploads,
  useVerwerpVoorbeeld,
  useVinkMelding,
} from '@/lib/rooster-api'
import type { Afvinkantwoord, Melding, Upload, Voorbeeld } from '@/lib/rooster-types'
import { cn } from '@/lib/utils'

const EERSTE_UPLOADS = 5

export function RoosterPage() {
  const uploads = useUploads()
  const maakVoorbeeld = useMaakVoorbeeld()
  const bevestig = useBevestigVoorbeeld()
  const verwerp = useVerwerpVoorbeeld()
  const vink = useVinkMelding()

  const invoer = useRef<HTMLInputElement>(null)
  const [bestand, setBestand] = useState<File | null>(null)
  const [voorbeeld, setVoorbeeld] = useState<Voorbeeld | null>(null)
  const [resultaat, setResultaat] = useState<Voorbeeld | null>(null)

  function maak() {
    if (!bestand) return
    maakVoorbeeld.mutate(bestand, {
      onSuccess: (uitkomst) => {
        setVoorbeeld(uitkomst)
        setResultaat(null)
        setBestand(null)
        if (invoer.current) invoer.current.value = ''
      },
    })
  }

  function importeer() {
    const voorbeeldId = voorbeeld?.voorbeeldId
    if (!voorbeeld || !voorbeeldId) return
    bevestig.mutate(
      { voorbeeldId, bestandsnaam: voorbeeld.bestandsnaam },
      {
        onSuccess: (uitkomst) => {
          setResultaat(uitkomst)
          setVoorbeeld(null)
        },
      },
    )
  }

  function kiesAnderBestand() {
    if (voorbeeld?.voorbeeldId) verwerp.mutate(voorbeeld.voorbeeldId)
    setVoorbeeld(null)
  }

  function zetAfgevinkt(melding: Melding, aan: boolean) {
    vink.mutate(
      { sleutel: melding.sleutel, bericht: melding.bericht, afgevinkt: aan },
      { onSuccess: (antwoord) => verwerkAfvinking(melding.sleutel, antwoord) },
    )
  }

  function verwerkAfvinking(sleutel: string, antwoord: Afvinkantwoord | null) {
    const bijwerken = (verslag: Voorbeeld | null): Voorbeeld | null =>
      verslag === null
        ? null
        : {
            ...verslag,
            meldingen: verslag.meldingen.map((melding) =>
              melding.sleutel === sleutel
                ? {
                    ...melding,
                    afgevinkt: antwoord !== null,
                    afgevinktOp: antwoord?.afgevinktOp ?? null,
                    afgevinktDoor: antwoord?.afgevinktDoor ?? null,
                  }
                : melding,
            ),
          }

    setVoorbeeld(bijwerken)
    setResultaat(bijwerken)
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Rooster"
        description="Het roosterbestand van de roostermaker is de bron. Je ziet eerst wat een upload gaat wijzigen; pas daarna wordt het weggeschreven."
      />

      <section className="rounded-card bg-surface p-5 shadow-card">
        <h2 className="text-sm font-semibold text-ink">Roosterbestand inlezen</h2>
        <p className="mt-0.5 text-sm text-ink-faint">
          Kies het xlsx-bestand van de roostermaker. Maximaal 16 MB.
        </p>

        {voorbeeld ? (
          <Voorbeeldblok
            voorbeeld={voorbeeld}
            bezig={bevestig.isPending}
            onImporteer={importeer}
            onAnderBestand={kiesAnderBestand}
            onVink={zetAfgevinkt}
          />
        ) : (
          <div className="mt-4 flex flex-wrap items-center gap-3 rounded-card border border-dashed border-hairline bg-fill-1 px-4 py-4">
            <FileSpreadsheet className="h-5 w-5 shrink-0 text-ink-faint" strokeWidth={1.85} />

            <input
              ref={invoer}
              type="file"
              accept=".xlsx"
              aria-label="Roosterbestand"
              onChange={(gebeurtenis) => setBestand(gebeurtenis.target.files?.[0] ?? null)}
              className="min-w-0 flex-1 text-sm text-ink-soft file:mr-3 file:rounded-lg file:border file:border-hairline file:bg-surface file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-ink hover:file:bg-fill-2"
            />

            <button
              type="button"
              className="btn-primary"
              onClick={maak}
              disabled={!bestand || maakVoorbeeld.isPending}
            >
              <UploadIcoon className="h-4 w-4" />
              {maakVoorbeeld.isPending ? 'Bezig met lezen' : 'Voorbeeld bekijken'}
            </button>
          </div>
        )}

        {resultaat ? <Resultaatblok resultaat={resultaat} onVink={zetAfgevinkt} /> : null}
      </section>

      <section className="overflow-hidden rounded-card bg-surface shadow-card">
        <div className="flex items-baseline justify-between gap-3 border-b border-hairline px-5 py-4">
          <h2 className="text-sm font-semibold text-ink">Eerdere uploads</h2>
          <span className="text-xs text-ink-faint">De laatste vijftig</span>
        </div>

        {uploads.isError ? (
          <ErrorState error={uploads.error} onRetry={() => uploads.refetch()} compact />
        ) : uploads.isLoading ? (
          <div className="space-y-2 p-5">
            {Array.from({ length: 3 }).map((_, index) => (
              <Skeleton key={index} className="h-14 w-full" />
            ))}
          </div>
        ) : !uploads.data || uploads.data.length === 0 ? (
          <EmptyState
            icon={History}
            title="Nog geen uploads"
            description="Zodra je een roosterbestand inleest verschijnt het hier."
            className="m-5 border-0 bg-transparent py-10"
          />
        ) : (
          <Geschiedenis regels={uploads.data} />
        )}
      </section>
    </div>
  )
}

interface VoorbeeldblokProps {
  voorbeeld: Voorbeeld
  bezig: boolean
  onImporteer: () => void
  onAnderBestand: () => void
  onVink: (melding: Melding, aan: boolean) => void
}

function Voorbeeldblok({
  voorbeeld,
  bezig,
  onImporteer,
  onAnderBestand,
  onVink,
}: VoorbeeldblokProps) {
  const blokken = verdeelMeldingen(voorbeeld.meldingen)

  return (
    <div className="mt-4 overflow-hidden rounded-card border border-hairline">
      <div className="border-b border-hairline bg-fill-1 px-4 py-3">
        <h3 className="flex items-center gap-2 text-base font-semibold text-ink">
          <TriangleAlert className="h-5 w-5 shrink-0 text-status-onderbezet" strokeWidth={1.85} />
          Er is nog niets opgeslagen
        </h3>
        <p className="mt-1 text-sm text-ink-soft">
          Dit is wat {voorbeeld.bestandsnaam} gaat wijzigen. Pas als je op Importeren klikt wordt
          het weggeschreven.
        </p>
      </div>

      <Cijfers verslag={voorbeeld} />

      <Meldingblok titel="Aandacht" meldingen={blokken.aandacht} onVink={onVink} />
      <Meldingblok titel="Eerder al bekeken" meldingen={blokken.bekeken} gedempt onVink={onVink} />

      {voorbeeld.meldingen.length === 0 ? (
        <p className="border-t border-hairline px-4 py-3 text-sm text-ink-faint">Geen meldingen.</p>
      ) : null}

      <div className="flex flex-wrap gap-2 border-t border-hairline bg-fill-1 px-4 py-3">
        <button type="button" className="btn-primary" onClick={onImporteer} disabled={bezig}>
          <CircleCheck className="h-4 w-4" />
          {bezig ? 'Bezig met importeren' : 'Importeren'}
        </button>
        <button type="button" className="btn-secondary" onClick={onAnderBestand} disabled={bezig}>
          Ander bestand kiezen
        </button>
      </div>
    </div>
  )
}

interface ResultaatblokProps {
  resultaat: Voorbeeld
  onVink: (melding: Melding, aan: boolean) => void
}

function Resultaatblok({ resultaat, onVink }: ResultaatblokProps) {
  const blokken = verdeelMeldingen(resultaat.meldingen)

  return (
    <div className="mt-5 overflow-hidden rounded-card border border-hairline">
      <div className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline px-4 py-3">
        <span className="flex items-center gap-2 text-sm font-medium text-ink">
          <CircleCheck className="h-4 w-4 text-status-in-orde" strokeWidth={1.85} />
          {resultaat.bestandsnaam} is geïmporteerd
        </span>
        <span className="text-xs text-ink-faint">
          {resultaat.semesters.map((semester) => semester.naam).join(' · ')}
        </span>
      </div>

      <Cijfers verslag={resultaat} />

      <Meldingblok titel="Nog open" meldingen={blokken.aandacht} onVink={onVink} />
      <Meldingblok titel="Eerder al bekeken" meldingen={blokken.bekeken} gedempt onVink={onVink} />

      {resultaat.meldingen.length === 0 ? (
        <p className="border-t border-hairline px-4 py-3 text-sm text-ink-faint">Geen meldingen.</p>
      ) : null}
    </div>
  )
}

function Cijfers({ verslag }: { verslag: Voorbeeld }) {
  const cijfers = [
    { label: 'Nieuwe groepen', waarde: verslag.groepen, altijd: false },
    { label: 'Lesreeksen', waarde: verslag.lesreeksen, altijd: true },
    { label: 'Nieuwe medewerkers', waarde: verslag.medewerkers, altijd: false },
    { label: 'Nieuwe vakantieweken', waarde: verslag.vakantieweken, altijd: false },
  ].filter((cijfer) => cijfer.altijd || cijfer.waarde !== 0)

  const weekmutaties =
    verslag.wekenToegevoegd + verslag.wekenGewijzigd + verslag.wekenVervallen > 0

  return (
    <div>
      <div className="grid grid-cols-2 divide-x divide-y divide-hairline sm:grid-cols-4 sm:divide-y-0">
        {cijfers.map((cijfer) => (
          <div key={cijfer.label} className="px-4 py-3">
            <p className="label-caps">{cijfer.label}</p>
            <p className="num mt-0.5 text-left text-lg font-semibold text-ink">{cijfer.waarde}</p>
          </div>
        ))}
      </div>

      {weekmutaties ? (
        <p className="num border-t border-hairline px-4 py-2.5 text-left text-sm text-ink-soft">
          Lesweken: +{verslag.wekenToegevoegd} nieuw · {verslag.wekenGewijzigd} gewijzigd ·{' '}
          {verslag.wekenVervallen} vervallen
        </p>
      ) : null}
    </div>
  )
}

interface MeldingblokProps {
  titel: string
  meldingen: Melding[]
  gedempt?: boolean
  onVink: (melding: Melding, aan: boolean) => void
}

function Meldingblok({ titel, meldingen, gedempt, onVink }: MeldingblokProps) {
  if (meldingen.length === 0) return null

  return (
    <div className={cn('border-t border-hairline', gedempt ? 'bg-fill-1' : null)}>
      <p className={cn('label-caps px-4', gedempt ? 'pt-2.5 pb-0.5' : 'pt-3 pb-1')}>
        {titel} ({meldingen.length})
      </p>
      <ul className={gedempt ? 'pb-2' : 'pb-3'}>
        {meldingen.map((melding) => (
          <MeldingRegel
            key={melding.sleutel}
            melding={melding}
            gedempt={gedempt}
            onVink={onVink}
          />
        ))}
      </ul>
    </div>
  )
}

interface MeldingRegelProps {
  melding: Melding
  gedempt?: boolean
  onVink: (melding: Melding, aan: boolean) => void
}

function MeldingRegel({ melding, gedempt, onVink }: MeldingRegelProps) {
  const vinkId = useId()
  const opvallend = isOpvallend(melding)
  const Icoon = opvallend ? TriangleAlert : Info

  return (
    <li className="flex items-start gap-2 px-4">
      <Checkbox
        id={vinkId}
        checked={melding.afgevinkt}
        onChange={(aan) => onVink(melding, aan)}
        className={cn('shrink-0', gedempt ? 'h-7' : 'h-8')}
      />

      <label
        htmlFor={vinkId}
        className={cn(
          'flex flex-1 cursor-pointer items-start gap-2 text-sm leading-6',
          gedempt ? 'py-0.5 text-ink-faint' : 'py-1 text-ink-soft',
        )}
      >
        <Icoon
          className={cn(
            'mt-0.5 h-4 w-4 shrink-0',
            gedempt ? 'text-ink-faint' : opvallend ? 'text-status-onderbezet' : 'text-ink-faint',
          )}
          strokeWidth={1.85}
        />
        <span className={cn(opvallend && !gedempt ? 'text-status-onderbezet' : null)}>
          {melding.bericht}
          {melding.cel ? <span className="ml-1.5 text-ink-faint">{melding.cel}</span> : null}
        </span>
      </label>
    </li>
  )
}

function Geschiedenis({ regels }: { regels: Upload[] }) {
  const [meer, setMeer] = useState(false)
  const eerste = regels.slice(0, EERSTE_UPLOADS)
  const rest = regels.slice(EERSTE_UPLOADS)

  return (
    <div>
      <ul className="divide-y divide-hairline">
        {eerste.map((regel) => (
          <Uploadregel key={regel.id} regel={regel} />
        ))}
      </ul>

      {rest.length > 0 ? (
        <Uitklapper
          open={meer}
          onOpenChange={setMeer}
          label={meer ? 'Toon minder uploads' : 'Toon meer uploads'}
          variant="knop"
          knop={meer ? 'Toon minder' : `Toon meer (${rest.length})`}
          knopClassName="w-full justify-center rounded-none border-t border-hairline py-3"
        >
          <ul className="divide-y divide-hairline border-t border-hairline">
            {rest.map((regel) => (
              <Uploadregel key={regel.id} regel={regel} />
            ))}
          </ul>
        </Uitklapper>
      ) : null}
    </div>
  )
}

function Uploadregel({ regel }: { regel: Upload }) {
  const [open, setOpen] = useState(false)
  const moment = regel.geuploadOp
    ? format(parseISO(regel.geuploadOp), 'd MMM yyyy HH:mm', { locale: nl })
    : 'onbekend'

  return (
    <li className="px-5 py-3">
      <Uitklapper
        open={open}
        onOpenChange={setOpen}
        label={open ? 'Waarschuwingen verbergen' : 'Waarschuwingen tonen'}
        variant="knop"
        knop={
          <>
            <TriangleAlert className="h-3.5 w-3.5" strokeWidth={1.85} />
            {regel.waarschuwingen.length}
          </>
        }
        knopClassName="text-status-onderbezet"
        paneelClassName="mt-2 border-t border-hairline pt-2"
        kop={(knop) => (
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="min-w-0">
              <span className="block truncate text-sm text-ink">{regel.bestandsnaam}</span>
              <span className="block text-xs text-ink-faint">
                {[moment, regel.geuploadDoor].filter(Boolean).join(' · ')}
              </span>
            </span>

            <span className="flex items-center gap-3 text-xs text-ink-faint">
              <span className="num">{regel.aantalLesreeksen} lesreeksen</span>
              <span className="num">{regel.aantalMedewerkers} medewerkers</span>
              {regel.waarschuwingen.length > 0 ? knop : null}
            </span>
          </div>
        )}
      >
        <ul className="space-y-1">
          {regel.waarschuwingen.map((waarschuwing, index) => (
            <li key={index} className="text-sm text-ink-soft">
              {waarschuwing.cel ? (
                <span className="text-ink-faint">{waarschuwing.cel} </span>
              ) : null}
              {waarschuwing.bericht}
            </li>
          ))}
        </ul>
      </Uitklapper>
    </li>
  )
}
